/**
 * A plain next step for an iQ error (F10): what staff should do about the
 * message iQ (or the connection to it) gave. Used by the application detail,
 * the iQ problems page and the Teams failure line, so they all say the same.
 * Unknown messages get a general hint; iQ's own words are always shown too.
 */

interface Hint {
  test: RegExp;
  hint: string;
  /** The answer to correct before sending again, when the hint names one. */
  answer?: string;
}

const HINTS: Hint[] = [
  {
    test: /timed? ?out|timeout|abort/i,
    hint: 'iQ didn’t answer in time. This usually clears by itself. Send it again.',
  },
  {
    test: /redirect|access|unauthori[sz]ed|forbidden|\b40[13]\b|token/i,
    hint: 'JFH Forms couldn’t sign in to iQ. An admin needs to check the iQ connection in Settings, then send it again.',
  },
  {
    test: /no intactiq record from the first stage/i,
    hint: 'The trade account was never set up in iQ, so there was nothing to add credit terms to. Send the trade account to iQ first.',
  },
  {
    test: /already exists|duplicate|in use/i,
    hint: 'iQ already has a record like this one. Check iQ for the existing account before sending again.',
  },
  {
    test: /\bvat/i,
    hint: 'The VAT number looks wrong. Correct it on the application, then send again.',
    answer: 'VAT number',
  },
  {
    test: /post ?code/i,
    hint: 'The postcode looks wrong. Correct the address on the application, then send again.',
    answer: 'postcode',
  },
  {
    test: /e-?mail/i,
    hint: 'The email address looks wrong. Correct it on the application, then send again.',
    answer: 'email address',
  },
  {
    test: /phone|telephone|mobile/i,
    hint: 'A phone number looks wrong. Correct it on the application, then send again.',
    answer: 'phone number',
  },
  {
    test: /\b50[0-4]\b|server error|unavailable|bad gateway/i,
    hint: 'iQ had a problem of its own. Wait a few minutes, then send it again.',
  },
];

const FALLBACK = 'Fix the cause, then send it again.';

export function iqHint(message: string | null | undefined): string {
  if (!message) return 'iQ didn’t say why. Send it again, and if it fails again, check the record in iQ.';
  return HINTS.find((h) => h.test.test(message))?.hint ?? FALLBACK;
}

/** The answer iQ's message points at ("postcode"), when it names one. */
export function iqHintAnswer(message: string | null | undefined): string | null {
  if (!message) return null;
  return HINTS.find((h) => h.test.test(message))?.answer ?? null;
}
